import React, { useState, useEffect } from 'react'
import { getServerSessionId } from '../../lib/serverSession'

interface TestUser {
  id: number
  username: string
  email: string
  full_name?: string
  created_at: string
  is_active: boolean
  total_shares: number
  total_spent: number
  commission_earned: number
}

interface AuditData {
  testUsers: TestUser[]
  totalTestUsers: number
  totalSharesPurchased: number
  totalAmountSpent: number
  totalCommissionsEarned: number
}

export const TestUserAuditReport: React.FC = () => {
  const [auditData, setAuditData] = useState<AuditData>({
    testUsers: [],
    totalTestUsers: 0,
    totalSharesPurchased: 0,
    totalAmountSpent: 0,
    totalCommissionsEarned: 0
  })
  const [loading, setLoading] = useState(true)
  const [selectedUser, setSelectedUser] = useState<TestUser | null>(null)

  useEffect(() => {
    loadAuditData()
  }, [])

  const loadAuditData = async () => {
    setLoading(true)
    try {
      const sessionId = getServerSessionId()
      const response = await fetch('/api/admin/test-user-audit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId || '' },
        body: JSON.stringify({ operation: 'load_audit_data' })
      })
      const result = await response.json()
      if (!result.success) throw new Error(result.error)
      setAuditData(result.auditData)
    } catch (error) {
      console.error('Error loading audit data:', error)
    } finally {
      setLoading(false)
    }
  }

  const deleteTestUser = async (userId: number) => {
    if (!confirm('Are you sure you want to delete this test user? This action cannot be undone.')) {
      return
    }

    try {
      const sessionId = getServerSessionId()
      const response = await fetch('/api/admin/test-user-audit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId || '' },
        body: JSON.stringify({ operation: 'delete_test_user', data: { userId } })
      })
      const result = await response.json()
      if (!result.success) throw new Error(result.error)

      alert('Test user deleted successfully!')
      loadAuditData()
    } catch (error) {
      console.error('Error deleting test user:', error)
      alert('Error deleting test user: ' + (error as Error).message)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-white">Loading audit data...</div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">Test User Audit Report</h2>
        <button
          onClick={loadAuditData}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
        >
          Refresh Data
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-gray-800 p-6 rounded-lg border border-gray-600">
          <h3 className="text-lg font-semibold text-white mb-2">Total Test Users</h3>
          <p className="text-3xl font-bold text-blue-400">{auditData.totalTestUsers}</p>
        </div>
        <div className="bg-gray-800 p-6 rounded-lg border border-gray-600">
          <h3 className="text-lg font-semibold text-white mb-2">Total Shares</h3>
          <p className="text-3xl font-bold text-green-400">{auditData.totalSharesPurchased.toLocaleString()}</p>
        </div>
        <div className="bg-gray-800 p-6 rounded-lg border border-gray-600">
          <h3 className="text-lg font-semibold text-white mb-2">Total Spent</h3>
          <p className="text-3xl font-bold text-yellow-400">${auditData.totalAmountSpent.toFixed(2)}</p>
        </div>
        <div className="bg-gray-800 p-6 rounded-lg border border-gray-600">
          <h3 className="text-lg font-semibold text-white mb-2">Commissions Earned</h3>
          <p className="text-3xl font-bold text-purple-400">${auditData.totalCommissionsEarned.toFixed(2)}</p>
        </div>
      </div>

      {/* Test Users Table */}
      <div className="bg-gray-800 rounded-lg border border-gray-600 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-600">
          <h3 className="text-xl font-semibold text-white">Test Users</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-300 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-300 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-300 uppercase tracking-wider">
                  Shares
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-300 uppercase tracking-wider">
                  Total Spent
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-300 uppercase tracking-wider">
                  Commissions
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-600">
              {auditData.testUsers.map((user) => (
                <tr key={user.id} className="hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-white">{user.username}</div>
                      <div className="text-sm text-white">ID: {user.id}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                    {user.email}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                    {user.total_shares.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                    ${user.total_spent.toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                    ${user.commission_earned.toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-sm font-semibold rounded-full ${
                      user.is_active
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setSelectedUser(user)}
                        className="text-blue-400 hover:text-blue-300"
                      >
                        View Details
                      </button>
                      <button
                        onClick={() => deleteTestUser(user.id)}
                        className="text-red-400 hover:text-red-300"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* User Details Modal */}
      {selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 p-6 rounded-lg border border-gray-600 max-w-2xl w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-white">User Details</h3>
              <button
                onClick={() => setSelectedUser(null)}
                className="text-gray-400 hover:text-white"
              >
                ✕
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-gray-300 mb-1">Username</label>
                <p className="text-white">{selectedUser.username}</p>
              </div>
              <div>
                <label className="block text-gray-300 mb-1">Email</label>
                <p className="text-white">{selectedUser.email}</p>
              </div>
              <div>
                <label className="block text-gray-300 mb-1">Full Name</label>
                <p className="text-white">{selectedUser.full_name || 'Not provided'}</p>
              </div>
              <div>
                <label className="block text-gray-300 mb-1">Created At</label>
                <p className="text-white">{new Date(selectedUser.created_at).toLocaleString()}</p>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-gray-300 mb-1">Total Shares</label>
                  <p className="text-white font-semibold">{selectedUser.total_shares.toLocaleString()}</p>
                </div>
                <div>
                  <label className="block text-gray-300 mb-1">Total Spent</label>
                  <p className="text-white font-semibold">${selectedUser.total_spent.toFixed(2)}</p>
                </div>
                <div>
                  <label className="block text-gray-300 mb-1">Commissions</label>
                  <p className="text-white font-semibold">${selectedUser.commission_earned.toFixed(2)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
